// Hospitality H1 (Sep 2026) — the public page a hotel sends its guests:
// /dine/:hostSlug. Cuisine → participating restaurant → the restaurant's
// EXISTING storefront. The only thing this page creates is a server-side
// referral; menu, cart, checkout, payment and delivery are the
// restaurant's own unchanged Sales Order flow.
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, MapPin, UtensilsCrossed } from "lucide-react";
import { createHostReferral, fetchHostPage, formatDestinationLines } from "../../lib/hostReferral";

export default function HostGuestLanding() {
  const { hostSlug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [cuisineKey, setCuisineKey] = useState(null);
  const [opening, setOpening] = useState(null);
  const [openError, setOpenError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchHostPage(hostSlug)
      .then((data) => {
        if (cancelled) return;
        setPage(data);
        if ((data.cuisines || []).length === 1) setCuisineKey(data.cuisines[0].key);
      })
      .catch(() => { if (!cancelled) setLoadError("This ordering link isn't active."); });
    return () => { cancelled = true; };
  }, [hostSlug]);

  const viewMenu = async (restaurant) => {
    setOpenError("");
    setOpening(restaurant.participation_id);
    try {
      const ref = await createHostReferral(hostSlug, restaurant.participation_id);
      navigate(`/retail/${encodeURIComponent(ref.storefront_slug)}?host_ref=${encodeURIComponent(ref.referral_token)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setOpenError((detail && detail.message) || "That restaurant isn't available right now.");
      setOpening(null);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#faf7f0] grid place-items-center px-6 text-center" data-testid="host-guest-error">
        <div>
          <UtensilsCrossed className="mx-auto h-10 w-10 text-stone-400" aria-hidden="true" />
          <p className="mt-3 text-stone-700">{loadError}</p>
        </div>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="min-h-screen bg-[#faf7f0] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-500" aria-label="Loading" />
      </div>
    );
  }

  const cuisines = page.cuisines || [];
  const selected = cuisines.find((c) => c.key === cuisineKey) || null;
  const addressLines = formatDestinationLines(page);

  return (
    <div className="min-h-screen bg-[#faf7f0] text-stone-900" data-testid="host-guest-landing">
      <header className="bg-[#0d1f12] text-stone-50">
        <div className="mx-auto max-w-xl px-4 py-7">
          <div className="text-[11px] uppercase tracking-[0.2em] text-orange-300">Guest food ordering</div>
          <h1 className="mt-1 font-serif text-3xl leading-tight" data-testid="host-guest-name">{page.host_display_name}</h1>
          <p className="mt-2 text-stone-200">Order food for delivery to our {String(page.receiving_point || "Front Desk").toLowerCase()}.</p>
          <div className="mt-3 flex items-start gap-1.5 text-xs text-stone-300">
            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            <span>{addressLines.join(" · ")}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-6">
        {cuisines.length === 0 ? (
          <p className="text-stone-600" data-testid="host-guest-empty">No restaurants are taking orders here right now.</p>
        ) : (
          <>
            <h2 className="font-serif text-xl">What would you like?</h2>
            <div className="mt-3 grid grid-cols-2 gap-3" role="list">
              {cuisines.map((c) => (
                <button
                  key={c.key} type="button" role="listitem"
                  onClick={() => setCuisineKey(c.key)}
                  data-testid={`host-cuisine-${c.key}`}
                  className={"h-14 rounded-xl border text-base font-semibold transition "
                    + (cuisineKey === c.key
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-white border-stone-300 text-stone-800 hover:border-stone-400")}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {selected && (
              <section className="mt-7" data-testid="host-restaurant-list">
                <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">{selected.label}</h3>
                <div className="mt-3 space-y-3">
                  {selected.restaurants.map((r) => (
                    <div key={r.participation_id} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm"
                      data-testid={`host-restaurant-${r.participation_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-serif text-lg leading-snug">{r.name}</div>
                          {r.description && <div className="mt-0.5 text-sm text-stone-600">{r.description}</div>}
                          {!r.open_now && <div className="mt-1 text-xs font-medium text-amber-700">Not taking delivery orders right now</div>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => viewMenu(r)}
                        disabled={opening !== null}
                        data-testid={`host-view-menu-${r.participation_id}`}
                        className="mt-3 w-full h-11 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-60"
                      >
                        {opening === r.participation_id ? "Opening menu…" : "View Menu"}
                      </button>
                    </div>
                  ))}
                </div>
                {openError && <p className="mt-3 text-sm text-rose-700" role="alert">{openError}</p>}
              </section>
            )}
          </>
        )}

        <footer className="mt-10 border-t border-stone-200 pt-4 text-xs leading-relaxed text-stone-500" data-testid="host-guest-notice">
          {page.fulfilment_notice}
          <div className="mt-2 text-stone-400">Ordering powered by Rudras-AI</div>
        </footer>
      </main>
    </div>
  );
}
